import { Button, Typography } from 'antd';
import {
  useChatAgentStatus,
  useGetShiftMeActive,
  useOmniChatSessionsProcessingList,
  useOmniChatSessionsWaitingList,
  useProfile
} from 'hooks';
import { omniChatMessages } from 'messages';
import { useReceiveChatSessionMutation } from 'services';
import { ChatSessionDto } from 'types';
import OmniChatSessionsProcessingItem from './OmniChatSessionsProcessingItem';
import { ChatIcon, HistoryRotateIcon, PlusIcon } from 'assets';
import { twMerge } from 'tailwind-merge';
import { ChannelObject, CHAT_AGENT_STATUS, configuration } from 'utils';
import { useCreateContactEventMutation } from 'services/tickets';
import { InView } from 'react-intersection-observer';
import OmniChatSessionsStatusToggle from './OmniChatSessionsStatusToggle';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

// Helper function to save contact response to session
export const saveContactResponseToSession = (chatSessionId: number, contactResponse: any) => {
  try {
    // Extract only the data field from the response
    const dataField = contactResponse.data;

    const storedSessions = localStorage.getItem('customerSupportSession');
    if (!storedSessions) {
      // If no sessions exist yet, create a new one with only the data field
      const newSession = {
        chatSessionId,
        searchData: [],
        type: 'chat',
        contactResponse: dataField // Store only the data field
      };
      localStorage.setItem('customerSupportSession', JSON.stringify([newSession]));

      return;
    }

    const sessions = JSON.parse(storedSessions);
    // Find the session for this chat
    const sessionIndex = sessions.findIndex((s: any) => s.chatSessionId === chatSessionId);

    if (sessionIndex >= 0) {
      // Update existing session with only data field
      sessions[sessionIndex].contactResponse = dataField;
    } else {
      // Create new session with only data field
      sessions.push({
        chatSessionId,
        searchData: [],
        type: 'chat',
        contactResponse: dataField
      });
    }

    // Save back to localStorage
    localStorage.setItem('customerSupportSession', JSON.stringify(sessions));
  } catch (error) {
    console.error('❌ Error saving contact response to session:', error);
  }
};

type OmniChatSessionsProcessingProps = {
  setChatSession: (value?: ChatSessionDto) => void;
  chatIdSession?: number;
  setOpenHistoryModal?: (value: boolean) => void;
};
export const getChannelIdByCode = (code: string): number | undefined => {
  const channelEntry = Object.entries(ChannelObject).find(
    ([_, channel]) => channel.code.toLowerCase() === code?.toLowerCase()
  );

  // Return the id of the found channel or default to LIVE_CHAT id
  if (channelEntry) {
    return channelEntry[1].id;
  }
};
const OmniChatSessionsProcessing: React.FC<OmniChatSessionsProcessingProps> = ({
  setChatSession,
  chatIdSession,
  setOpenHistoryModal
}) => {
  const { profile } = useProfile();
  const { data: chatAgentStatus } = useChatAgentStatus();
  const isReady = chatAgentStatus?.status === CHAT_AGENT_STATUS.READY;

  const location = useLocation();
  const navigate = useNavigate();
  const routeState: any = location.state;

  const { chatSessionProcessing, isLoading, hasMore, isFetching, handleLoadMore } = useOmniChatSessionsProcessingList({
    keyword: ''
  });

  const { chatSessionWaiting } = useOmniChatSessionsWaitingList({ keyword: '' });
  const [receiveChatSession, { isLoading: isLoadingReceive }] = useReceiveChatSessionMutation();
  const [onCreateContactEvent] = useCreateContactEventMutation();
  const { shiftMeActive } = useGetShiftMeActive();
  const limitSupport = configuration.OMNI_CHANNEL_CHAT_SESSION_LIMIT
    ? Number(configuration.OMNI_CHANNEL_CHAT_SESSION_LIMIT)
    : 0;
  const disabledReceive = chatSessionProcessing.length >= limitSupport || !chatSessionWaiting.length;

  useEffect(() => {
    if (routeState?.chatAISessionId) {
      setTimeout(() => {
        const findSession = chatSessionProcessing.find((item) => {
          return item.chatSessionId === routeState.chatAISessionId;
        });

        console.log('findSession', findSession, chatSessionProcessing);
        if (findSession) {
          setChatSession(findSession);
          // const channelId = getChannelIdByCode(findSession?.chat?.source);
          // if (profile?.extensionId && profile?.username && shiftMeActive?.shiftId && channelId) {
          //   onCreateContactEvent({
          //     agentId: profile?.userId,
          //     agentName: profile?.username,
          //     channelId: channelId,
          //     contactAccount: findSession?.chat?.customerId,
          //     contactId: findSession?.chat?.pageId,
          //     flowCode: 'Chat',
          //     shiftId: shiftMeActive?.shiftId,
          //     chatSessionId: findSession?.chatSessionId
          //   })
          //     .unwrap()
          //     .then((response) => {
          //       // Save response to session data
          //       const chatSessionId = findSession?.chatSessionId;
          //       saveContactResponseToSession(chatSessionId, response);
          //     })
          //     .catch((error) => {
          //       console.error('❌ Failed to create contact event:', error);
          //     });
          // }
          navigate(location.pathname, { replace: true, state: null });
        }
      }, 300);
    }
  }, [routeState?.chatAISessionId, chatSessionProcessing]);

  if (!chatSessionProcessing.length)
    return (
      <div className='relative h-full'>
        <div className={twMerge('mb-4 mt-5 flex w-full items-center gap-4 px-5')}>
          <Typography.Text type='secondary' className='text-xs font-semibold'>
            {profile?.shortName}
          </Typography.Text>
          <Button
            onClick={() => setOpenHistoryModal?.(true)}
            className='ml-auto border-0 bg-backgroundColor1'
            shape='circle'
            icon={<HistoryRotateIcon />}
          />
          <OmniChatSessionsStatusToggle />
        </div>

        <div className='absolute left-1/2 top-1/3 flex -translate-x-1/2 -translate-y-1/3 flex-col items-center'>
          <ChatIcon width={80} height={80} className='text-[#d4d8de]' />
          <Typography.Title level={5} className='mb-2 mt-5'>
            {omniChatMessages.notHaveChatSessions}
          </Typography.Title>
          <Typography.Text className='w-[280px] text-center'>
            {omniChatMessages.descriptionReceiveCustomer}
          </Typography.Text>
          <Button
            loading={isLoadingReceive}
            onClick={() => {
              receiveChatSession()
                .unwrap()
                .then((res) => {
                  const { data } = res;
                  if (data) {
                    const channelId = getChannelIdByCode(data?.chat?.source);
                    if (profile?.extensionId && profile?.username && shiftMeActive?.shiftId && channelId) {
                      onCreateContactEvent({
                        agentId: profile?.userId,
                        agentName: profile?.username,
                        channelId: channelId,
                        contactAccount: data?.chat?.customerId,
                        contactId: data?.chatSessionId.toString(),
                        flowCode: 'Chat',
                        shiftId: shiftMeActive?.shiftId,
                        chatSessionId: data?.chatSessionId
                      })
                        .unwrap()
                        .then((response) => {
                          // Save response to session data
                          const chatSessionId = data.chatSessionId;
                          saveContactResponseToSession(chatSessionId, response);
                        })
                        .catch((error) => {
                          console.error('❌ Failed to create contact event:', error);
                        });
                    }
                  }
                });
            }}
            type='primary'
            className='mt-8'
            disabled={disabledReceive || !isReady}
            icon={<PlusIcon />}
          >
            {omniChatMessages.receiveChat}
          </Button>
        </div>
      </div>
    );

  return (
    <div className='flex max-h-full flex-col overflow-hidden'>
      <div className='flex min-h-[98px] flex-col items-center px-5'>
        <div className={twMerge('mb-4 mt-5 flex w-full items-center justify-between')}>
          <Typography.Text type='secondary' className='text-xs font-semibold'>
            {profile?.shortName}
          </Typography.Text>
          <OmniChatSessionsStatusToggle />
        </div>

        <div className='flex w-full items-center gap-4'>
          <Typography.Text className='mb-0 text-lg font-semibold'>
            {omniChatMessages.customerBeingSupported} ({chatSessionProcessing.length})
          </Typography.Text>
          <Button
            onClick={() => setOpenHistoryModal?.(true)}
            className='ml-auto border-0 bg-backgroundColor1'
            shape='circle'
            icon={<HistoryRotateIcon />}
          />
          <Button
            disabled={disabledReceive || !isReady}
            onClick={() => {
              receiveChatSession()
                .unwrap()
                .then((res) => {
                  const { data } = res;
                  if (data) {
                    const channelId = getChannelIdByCode(data?.chat?.source);
                    if (profile?.extensionId && profile?.username && shiftMeActive?.shiftId && channelId) {
                      onCreateContactEvent({
                        agentId: profile?.userId,
                        agentName: profile?.username,
                        channelId: channelId,
                        contactAccount: data?.chat?.customerId,
                        contactId: data?.chatSessionId.toString(),
                        flowCode: 'Chat',
                        shiftId: shiftMeActive?.shiftId,
                        chatSessionId: data?.chatSessionId
                      })
                        .unwrap()
                        .then((response) => {
                          // Save response to session data
                          const chatSessionId = data.chatSessionId;
                          saveContactResponseToSession(chatSessionId, response);
                        })
                        .catch((error) => {
                          console.error('❌ Failed to create contact event:', error);
                        });
                    }
                  }
                });
            }}
            icon={<PlusIcon />}
            className={twMerge(
              'h-8 w-8 border-mainColor1 bg-white text-mainColor1',
              (disabledReceive || !isReady) && 'opacity-50'
            )}
            shape='circle'
          ></Button>
        </div>
      </div>

      <div className='max-h-full w-full overflow-y-auto overflow-x-hidden'>
        {chatSessionProcessing.map((item: ChatSessionDto) => (
          <OmniChatSessionsProcessingItem
            active={chatIdSession === item.chatId}
            key={item.chatSessionId}
            chatSession={item}
            onSelect={setChatSession}
          />
        ))}
        {!isLoading && hasMore && !isFetching ? (
          <InView className='h-10 w-full bg-red-50' as='div' onChange={(inView) => inView && handleLoadMore()} />
        ) : null}
      </div>
    </div>
  );
};

export default OmniChatSessionsProcessing;
